import React, { useEffect, useRef, useState } from "react";
import { ArcIcon, HeartImage, LungsImage, SleepImage, Spo2Image, StepsImage } from "./../../../assets/icons/index";

interface WellnessCircleProps {
  containerWidth?: number;
}

const WellnessCircle: React.FC<WellnessCircleProps> = ({ containerWidth }) => {
  const animationRef = useRef<number | null>(null);
  const radiusAnimationRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentAngle, setCurrentAngle] = useState(0);
  const [currentRadius, setCurrentRadius] = useState(0); // Start from 0
  const iconAngles = [0, 72, 144, 216, 288];
  // Responsive radius based on container size
  const getResponsiveRadius = () => {
    const screenWidth = containerWidth || window.innerWidth;
    if (screenWidth <= 320) return 80; // Very small screens
    if (screenWidth <= 375) return 80; // Small screens
    if (screenWidth <= 414) return 100; // Medium screens
    if (screenWidth <= 768) return 110; // Large phones
    return 120; // Tablets and larger
  };

  const [targetRadius, setTargetRadius] = useState(getResponsiveRadius());

  // Animate radius from 0 to target radius
  const animateRadius = () => {
    const target = getResponsiveRadius();
    setTargetRadius(target);
    setCurrentRadius(0); // Reset to 0

    // Clear any existing radius animation
    if (radiusAnimationRef.current) {
      clearInterval(radiusAnimationRef.current);
    }

    // Calculate increment step (target radius / number of steps)
    // We want smooth animation, so let's use smaller increments
    const totalDuration = 2000; // 2 seconds total animation
    const intervalTime = 10; // Update every 100ms
    const totalSteps = totalDuration / intervalTime; // 20 steps
    const increment = target / totalSteps;

    radiusAnimationRef.current = setInterval(() => {
      setCurrentRadius((prevRadius) => {
        const newRadius = prevRadius + increment;
        if (newRadius >= target) {
          if (radiusAnimationRef.current) {
            clearInterval(radiusAnimationRef.current);
            radiusAnimationRef.current = null;
          }
          return target;
        }
        return newRadius;
      });
    }, intervalTime);
  };

  const getCenterPosition = () => {
    if (!containerRef.current) return { x: 150, y: 150 };

    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: rect.width / 2,
      y: rect.height / 2,
    };
  };

  const updateIconPositions = () => {
    const center = getCenterPosition();
    const icons = document.querySelectorAll(".wellness-icon");
    icons.forEach((icon, index) => {
      const baseAngle = iconAngles[index];
      const currentIconAngle = (baseAngle + currentAngle) % 360;
      const angleInRadians = (currentIconAngle * Math.PI) / 180;
      const x = center.x + currentRadius * Math.cos(angleInRadians);
      const y = center.y + currentRadius * Math.sin(angleInRadians);
      (icon as HTMLElement).style.left = `${x - 24}px`; // 24px = half of icon width (48px)
      (icon as HTMLElement).style.top = `${y - 24}px`; // 24px = half of icon height (48px)
    });
  };

  const animate = () => {
    setCurrentAngle((prev) => {
      const newAngle = prev + 0.4 >= 360 ? 0 : prev + 0.4; // Slightly faster rotation
      return newAngle;
    });
    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    updateIconPositions();
  }, [currentAngle, currentRadius]);

  useEffect(() => {
    // Start radius animation on component mount
    animateRadius();

    // Start rotation animation
    animationRef.current = requestAnimationFrame(animate);

    const handleResize = () => {
      // Restart radius animation on resize
      animateRadius();
      updateIconPositions();
    };
    window.addEventListener("resize", handleResize);

    const handleVisibility = () => {
      if (document.hidden && animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      } else {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (radiusAnimationRef.current) {
        cancelAnimationFrame(radiusAnimationRef.current as unknown as number);
      }
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const getCenterIconSize = () => {
    const screenWidth = containerWidth || window.innerWidth;
    if (screenWidth <= 320) return "w-16 h-16"; // 64px
    if (screenWidth <= 375) return "w-18 h-18"; // 72px
    return "w-20 h-20"; // 80px
  };

  const getWellnessIconSize = () => {
    const screenWidth = containerWidth || window.innerWidth;
    if (screenWidth <= 320) return "w-10 h-10"; // 40px
    if (screenWidth <= 375) return "w-11 h-11"; // 44px
    return "w-14 h-14"; // 48px
  };

  const getImageSize = () => {
    const screenWidth = containerWidth || window.innerWidth;
    if (screenWidth <= 320) return "w-5 h-5"; // 20px
    if (screenWidth <= 375) return "w-6 h-6"; // 24px
    return "w-7 h-7"; // 28px
  };

  const getTextSize = () => {
    const screenWidth = containerWidth || window.innerWidth;
    if (screenWidth <= 320) return "text-[7px]";
    if (screenWidth <= 375) return "text-[7.5px]";
    return "text-[8px]";
  };

  return (
    <div className="flex justify-center bg-transparent items-center w-50">
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={{
          width: `${targetRadius * 3 + 160}px`,
          height: `${targetRadius * 3 + 120}px`,
          maxHeight: "50vh",
        }}
      >
        {/* Center Icon */}
        <div
          className={`absolute  flex flex-col justify-center items-center z-10`}
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <img
            src={ArcIcon}
            alt="Arc Icon"
            className={` object-contain rounded-full`}
            height={80}
            width={80}
          />
        </div>

        {/* Wellness Icons */}
        <div
          className={`wellness-icon absolute ${getWellnessIconSize()} rounded-full flex flex-col justify-center items-center shadow-md bg-white border-2 border-gray-200 steps`}
        >
          <img
            src={StepsImage}
            alt="Steps"
            className={`${getImageSize()} object-contain mb-[1px]`}
          />
          <div
            className={`${getTextSize()} font-bold text-gray-500 text-center leading-none`}
          ></div>
        </div>
        <div
          className={`wellness-icon absolute ${getWellnessIconSize()} rounded-full flex flex-col justify-center items-center shadow-md bg-white border-2 border-gray-200 heart`}
        >
          <img
            src={HeartImage}
            alt="Heart"
            className={`${getImageSize()} object-contain mb-[1px]`}
          />
          <div
            className={`${getTextSize()} font-bold text-gray-500 text-center leading-none`}
          ></div>
        </div>
        <div
          className={`wellness-icon absolute ${getWellnessIconSize()} rounded-full flex flex-col justify-center items-center shadow-md bg-white border-2 border-gray-200 sleep`}
        >
          <img
            src={SleepImage}
            alt="Sleep"
            className={`${getImageSize()} object-contain mb-[1px]`}
          />
          <div
            className={`${getTextSize()} font-bold text-gray-500 text-center leading-none`}
          ></div>
        </div>
        <div
          className={`wellness-icon absolute ${getWellnessIconSize()} rounded-full flex flex-col justify-center items-center shadow-md bg-white border-2 border-gray-200 spo2`}
        >
          <img
            src={Spo2Image}
            alt="SPO2"
            className={`${getImageSize()} object-contain mb-[1px]`}
          />
          <div
            className={`${getTextSize()} font-bold text-gray-500 text-center leading-none`}
          ></div>
        </div>
        <div
          className={`wellness-icon absolute ${getWellnessIconSize()} rounded-full flex flex-col justify-center items-center shadow-md bg-white border-2 border-gray-200 medication`}
        >
          <img
            src={LungsImage}
            alt="Medication"
            className={`${getImageSize()} object-contain mb-[1px]`}
          />
          <div
            className={`${getTextSize()} font-bold text-gray-500 text-center leading-none`}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default WellnessCircle;
